import React, { SVGProps, useEffect, useMemo } from 'react';
import {
  AreaChart,
  YAxis,
  AreaProps,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  TooltipProps,
  XAxis,
} from 'recharts';
import clsx from 'clsx';
import useMediaQuery from '../../hooks/useMediaQuery';
import { ceilNumberToTwoDigit, getMaxValue } from '../../helpers';
import {
  CheckboxChecked24Filled,
  CheckboxUnchecked24Regular,
} from '@fluentui/react-icons';
import { DataKey } from 'recharts/types/util/types';

type AreaPropsType = Omit<SVGProps<SVGElement>, 'type'> & AreaProps;

interface ChartAreaProps {
  valueFormatter?: (value: number, payload?: any) => string;
  data: any[];
  children: React.ReactElement<AreaPropsType>[];
  tickFormatter?: (value: number) => string;
  rightYAxis?: boolean;
  rightTricks?: (string | number)[];
  classNameHeader?: string;
  classNameChart?: string;
  onDataKeysToggle?: (values: DataKey<any>[]) => void;
}

interface CustomTooltipProps extends TooltipProps<any, any> {
  valueFormatter?: (value: number, payload?: any) => string;
  rightYAxis?: boolean;
}

const CustomTooltip: React.FC<CustomTooltipProps> = React.memo(
  ({ active, payload, valueFormatter, rightYAxis }) => {
    if (!active || !payload || payload.length === 0) return null;

    return (
      <div className="p-4 !pt-3 bg-white border  shadow-md rounded-3xl">
        <p className="text-sm font-semibold text-black">
          {payload[0]?.payload?.largeName || payload[0]?.payload?.name}
        </p>
        {payload.map((p, k) => {
          const isStaff =
            rightYAxis && payload.length - 2 === k && p.name === 'STAFF';
          const isTotal = rightYAxis && payload.length - 1 === k;

          return (
            <p
              key={k}
              style={{ color: isTotal ? 'black' : p.stroke }}
              className={clsx('py-1 text-sm font-semibold', {
                'font-semibold': isStaff,
              })}
            >
              {`${p.name}: ${
                valueFormatter ? valueFormatter(p.value, p) : p.value
              }`}
            </p>
          );
        })}
      </div>
    );
  },
);

CustomTooltip.displayName = 'CustomTooltip';

const CustomActiveDot: React.FC<SVGProps<SVGCircleElement>> = (props) => {
  const { cx, cy, fill } = props;
  return (
    <g>
      {/* Outer Transparent Ring */}
      <circle cx={cx} cy={cy} r={10} fill={fill} opacity={0.15} />

      {/* Middle White Ring */}
      <circle cx={cx} cy={cy} r={6} fill="white" />

      {/* Inner Solid Green Circle */}
      <circle cx={cx} cy={cy} r={4} fill={fill} />
    </g>
  );
};

export const ChartArea: React.FC<ChartAreaProps> = ({
  valueFormatter,
  data,
  children,
  tickFormatter,
  rightYAxis,
  rightTricks,
  classNameHeader,
  classNameChart,
  onDataKeysToggle, // Updated prop
}) => {
  // Initialize disableAreaIds as an empty array to display all charts by default
  const [disableAreaIds, setDisableAreaIds] = React.useState<DataKey<any>[]>(
    [],
  );
  const areaProps = useMemo(
    () => React.Children.map(children, (child) => child.props) || [],
    [children],
  );

  const isMobile = useMediaQuery('(max-width: 768px)');

  const filteredData = useMemo(() => {
    return data.map((element) => {
      const clone = { ...element };
      Object.keys(clone).forEach((key) => {
        if (disableAreaIds.includes(key)) {
          delete clone[key];
        }
      });
      return clone;
    });
  }, [data, disableAreaIds]);

  const maxValue = useMemo(
    () => ceilNumberToTwoDigit(getMaxValue(filteredData)),
    [filteredData],
  );
  const ticks = useMemo(() => {
    if (isNaN(maxValue)) return [];
    return Array.from({ length: 5 }, (_, item) => ((item + 1) * maxValue) / 5);
  }, [maxValue]);

  const toggleArea = (id: DataKey<any>) => {
    setDisableAreaIds((prev) => {
      const newAreaIds = prev.includes(id)
        ? prev.filter((areaId) => areaId !== id)
        : [...prev, id];
      return newAreaIds;
    });
  };

  useEffect(() => {
    if (onDataKeysToggle) {
      const enabledAreaIds = areaProps
        .map((area) => area.dataKey.toString())
        .filter((id) => !disableAreaIds.includes(id));
      onDataKeysToggle(enabledAreaIds);
    }
  }, [disableAreaIds, areaProps, onDataKeysToggle]);

  return (
    <div>
      <div
        className={clsx(
          'flex items-center justify-end gap-8 ',
          classNameHeader,
        )}
      >
        {areaProps.map((area, index) => {
          if (area.dataKey === 'TOTAL_PAYROLL') return null;

          const isChecked = !disableAreaIds.includes(area.dataKey);
          return (
            <label
              key={`${area.dataKey.toString()}-${index}`}
              className="flex items-center gap-2 cursor-pointer"
              style={{ color: area.stroke }}
            >
              {isChecked ? (
                <CheckboxChecked24Filled className="w-6 h-6" />
              ) : (
                <CheckboxUnchecked24Regular className="w-6 h-6" />
              )}
              <span className="hidden text-sm font-medium text-gray-700 md:block">
                {area.label}
              </span>
              <input
                type="checkbox"
                checked={isChecked}
                onChange={() => toggleArea(area.dataKey)}
                className="hidden"
              />
            </label>
          );
        })}
      </div>
      <div className={clsx('w-full h-[400px]', classNameChart)}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={filteredData}
            margin={{ top: 10, right: 0, left: 0, bottom: 0 }}
          >
            <CartesianGrid
              horizontal={true}
              vertical={false}
              stroke="#F1F0F4"
            />
            <XAxis
              interval="preserveStartEnd"
              dataKey={isMobile && data.length > 6 ? 'smName' : 'name'}
              axisLine={false}
              tickLine={false}
            />
            <YAxis
              tickFormatter={tickFormatter}
              ticks={ticks}
              type="number"
              axisLine={false}
              tickLine={false}
            />
            {rightYAxis && (
              <YAxis
                ticks={rightTricks}
                type="number"
                dataKey="right"
                orientation="right"
                yAxisId="right"
                axisLine={false}
                tickLine={false}
              />
            )}
            <Tooltip
              content={(props) => (
                <CustomTooltip
                  {...props}
                  rightYAxis={rightYAxis}
                  valueFormatter={valueFormatter}
                />
              )}
            />
            {React.Children.map(children, (child) =>
              !disableAreaIds.includes(child.props.dataKey)
                ? React.cloneElement(child, {
                    key: child.props.dataKey.toString(),
                    activeDot: <CustomActiveDot />,
                  })
                : null,
            )}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};
